import { NodeSelection, TextSelection } from '@tiptap/pm/state';
import { useEditorState, type Editor } from '@tiptap/react';
import { AlignCenterIcon, AlignJustifyIcon, AlignLeftIcon, AlignRightIcon } from 'lucide-react';
import { useCallback, useMemo } from 'react';

import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useTiptapEditor } from '@/hooks/tiptap';
import { shouldShow } from '@/lib/tiptap';
import { hasExtension } from '@/lib/tiptap/tiptapUtils';

import type { TextAlign, TextAlignButtonProps } from '.';

const TEXT_ALIGN_ICONS = {
  left: AlignLeftIcon,
  center: AlignCenterIcon,
  right: AlignRightIcon,
  justify: AlignJustifyIcon,
};

const TEXT_ALIGN_SHORTCUTS: Partial<Record<TextAlign, string>> = {
  left: 'Ctrl-Shift-l',
  center: 'Ctrl-Shift-e',
  right: 'Ctrl-Shift-r',
  justify: 'Ctrl-Shift-j',
};

const TEXT_ALIGN_LABELS: Record<TextAlign, string> = {
  left: 'Align left',
  center: 'Align center',
  right: 'Align right',
  justify: 'Align justify',
};

function getTextAlignTypes(editor: Editor): string[] {
  const schemaHasType = (type: string): boolean => Object.prototype.hasOwnProperty.call(editor.schema.nodes, type);

  const extension = editor.extensionManager.extensions.find((ext) => ext.name === 'textAlign');

  const types = extension?.options?.types;
  const filteredTypes = (Array.isArray(types) ? types : []).filter((type): type is string => typeof type === 'string').filter((type) => schemaHasType(type));

  if (filteredTypes.length > 0) {
    return filteredTypes;
  }

  const fallbackTypes = ['paragraph', 'heading'];
  return fallbackTypes.filter((type) => schemaHasType(type));
}

function isSelectionInAlignableNode(editor: Editor, alignableTypes: string[]): boolean {
  if (!alignableTypes.length) return false;

  const { state } = editor;
  const { selection } = state;

  if (selection instanceof NodeSelection) {
    return alignableTypes.includes(selection.node.type.name);
  }

  if (selection instanceof TextSelection && selection.empty) {
    return alignableTypes.includes(selection.$from.parent.type.name);
  }

  let canAlign = false;
  const { from, to } = selection;

  state.doc.nodesBetween(from, to, (node) => {
    if (node.isTextblock && alignableTypes.includes(node.type.name)) {
      canAlign = true;
      return false;
    }
    return undefined;
  });

  return canAlign;
}

function useCanSetTextAlign(editor: Editor | null): boolean {
  return (
    useEditorState({
      editor,
      selector: (ctx) => {
        if (!ctx.editor) return false;
        if (!hasExtension(ctx.editor, 'textAlign')) return false;

        const alignableTypes = getTextAlignTypes(ctx.editor);
        return isSelectionInAlignableNode(ctx.editor, alignableTypes);
      },
    }) ?? false
  );
}

function useIsTextAlignActive(editor: Editor | null, align: TextAlign): boolean {
  return (
    useEditorState({
      editor,
      selector: (ctx) =>
        ctx.editor?.isActive({
          textAlign: align,
        }),
    }) ?? false
  );
}

function setTextAlign(editor: Editor | null, align: TextAlign): boolean {
  if (!editor) return false;
  if (!hasExtension(editor, 'textAlign')) {
    return false;
  }
  return editor.chain().focus().setTextAlign(align).run();
}

function isTextAlignButtonDisabled(editor: Editor | null, canAlign: boolean, userDisabled: boolean = false): boolean {
  if (!editor) return true;
  if (userDisabled) return true;
  if (!canAlign) return true;
  return false;
}

function useTextAlign(editor: Editor | null, align: TextAlign, disabled: boolean = false) {
  const canAlign: boolean = useCanSetTextAlign(editor);

  const isDisabled: boolean = isTextAlignButtonDisabled(editor, canAlign, disabled);
  const isActive: boolean = useIsTextAlignActive(editor, align);

  const handleAlignment = useCallback((): boolean => {
    if (!editor || isDisabled) return false;
    return setTextAlign(editor, align);
  }, [editor, isDisabled, align]);

  const show: boolean = useMemo(() => shouldShow([!!editor, hasExtension(editor, 'textAlign')]), [editor]);

  const Icon = TEXT_ALIGN_ICONS[align];
  const shortcutKey: string | undefined = TEXT_ALIGN_SHORTCUTS[align];
  const label: string = TEXT_ALIGN_LABELS[align];

  return {
    canAlign,
    isDisabled,
    isActive,
    handleAlignment,
    show,
    Icon,
    shortcutKey,
    label,
  };
}

function useTextAlignButtonClick(onClick: TextAlignButtonProps['onClick'], callback: () => void) {
  return useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      onClick?.(e);
      if (!e.defaultPrevented) {
        callback();
      }
    },
    [onClick, callback]
  );
}

export function TextAlignButton(textAlignButtonProps: TextAlignButtonProps) {
  const { editor: providedEditor, disabled, align, onClick, children, ...props } = textAlignButtonProps;
  const editor: Editor | null = useTiptapEditor(providedEditor);

  const { isDisabled, isActive, handleAlignment, show, Icon, shortcutKey, label } = useTextAlign(editor, align, disabled);

  const handleClick = useTextAlignButtonClick(onClick, handleAlignment);

  if (!show || !editor || !editor.isEditable) {
    return null;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          data-slot="text-align-button"
          disabled={isDisabled}
          data-active-state={isActive ? 'on' : 'off'}
          data-disabled={isDisabled}
          aria-label={label}
          aria-pressed={isActive}
          onClick={handleClick}
          {...props}
        >
          {children || <Icon />}
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        {label}
        {shortcutKey && <p className="text-xs opacity-70">{shortcutKey}</p>}
      </TooltipContent>
    </Tooltip>
  );
}
